import './EditKpiMapping.scss';
import { useState, useCallback, useContext, useMemo } from 'react';
import * as R from 'ramda';
import * as uuid from 'uuid';
import { MdDelete, MdSave, MdAdd, MdEdit, MdSend } from 'react-icons/md';
import {
  KpiMappings,
  KpiEdits,
  SourceMapping,
  Status
} from '@blisspointmedia/bpm-types/dist/KpiMapping';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Brand50, Brand60 } from '@src/utils/colors';
import { useSetAreYouSure, useSetError } from '../../redux/modals';
import { Button } from '../../Components/Button';
import { Appearance, Placement, Size } from '../../Components/designConstants';
import { awaitJSON, CrossChannelLambdaFetch } from '../../utils/fetch-utils';
import { SourceColumn } from './SourceColumn';
import { CrossChannelKpiColumn } from './CrossChannelKpiColumn';
import {
  KpiMappingsContext,
  HandleCrossChannelKpiNameEdit,
  HandleLagEditInput,
  HandleKpiEditInput,
  HandleNewRowInput,
  CombinedCrossChannelKpis
} from './kpi-mapping-types';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import { useAppSelector } from '../../redux';
import * as UserRedux from '../../redux/user';

interface EditKpiMappingProps {
  company: string;
  data: KpiMappings;
  setEditMode: (editMode: boolean) => void;
  setKpiMappings: (kpiMappings: KpiMappings | undefined) => void;
}

const makeNewRowEditKey = (mapping: SourceMapping): string =>
  `${mapping.crossChannelKpiId}_${mapping.source}_${mapping.accountId}_${mapping.kpi}`;

const EditKpiMapping = ({ company, data, setEditMode, setKpiMappings }: EditKpiMappingProps) => {
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();

  const { sourcesWithKpis, accountInfoBySource, editMode } = useContext(KpiMappingsContext);

  const [kpiEdits, setKpiEdits] = useState<KpiEdits>({});
  const [crossChannelKpiNameEdits, setCrossChannelKpiNameEdits] = useState<Record<string, string>>(
    {}
  );
  const [newRows, setNewRows] = useState<KpiMappings>({});
  const [lagEdits, setLagEdits] = useState<Record<string, string>>({});
  const [crossChannelKpisToDelete, setCrossChannelKpisToDelete] = useState<Record<string, number>>(
    {}
  );
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const handleLagEdit = useCallback(({ source, lag }: HandleLagEditInput) => {
    setLagEdits(prevEdits => ({
      ...prevEdits,
      [source]: lag
    }));
  }, []);

  const handleCrossChannelKpiNameEdit = useCallback(
    ({ id, name }: HandleCrossChannelKpiNameEdit) => {
      setCrossChannelKpiNameEdits(prevEdits => ({
        ...prevEdits,
        [id]: name
      }));
    },
    []
  );

  const handleKpiEdit = useCallback(({ key, mappings }: HandleKpiEditInput) => {
    setKpiEdits(prevEdits => ({
      ...prevEdits,
      [key]: mappings
    }));
  }, []);

  const handleNewRow = useCallback(
    ({
      crossChannelKpiId,
      kpiLabel,
      sourceLabel,
      isCrossChannelKpiLabel,
      sourceMappings
    }: HandleNewRowInput) => {
      if (isCrossChannelKpiLabel) {
        setNewRows(prevNewRows => ({
          ...prevNewRows,
          [crossChannelKpiId]: {
            ...prevNewRows[crossChannelKpiId],
            crossChannelKpiLabel: kpiLabel
          }
        }));
      } else {
        setNewRows(prevNewRows => {
          const mappingsToRemoveMap = (sourceMappings || []).reduce<Record<string, boolean>>(
            (prev, curr) => {
              if (curr.status === Status.DELETE) {
                const key = makeNewRowEditKey(curr);
                return { ...prev, [key]: true };
              }
              return prev;
            },
            {}
          );

          const mappingsToAdd = (sourceMappings || []).filter(row => row.status === Status.INSERT);

          const existingMappings: SourceMapping[] =
            prevNewRows[crossChannelKpiId]?.mappings?.[sourceLabel] || [];
          const resolvedSourceMappings: SourceMapping[] = [
            ...existingMappings.filter(item => !mappingsToRemoveMap[makeNewRowEditKey(item)]),
            ...mappingsToAdd
          ];

          return {
            ...prevNewRows,
            [crossChannelKpiId]: {
              ...prevNewRows[crossChannelKpiId],
              mappings: {
                ...prevNewRows[crossChannelKpiId]?.mappings,
                [sourceLabel]: resolvedSourceMappings
              }
            }
          };
        });
      }
    },
    []
  );

  const addNewRow = useCallback(() => {
    const newRowKey = uuid.v4();

    setNewRows(prevNewRows => ({
      ...prevNewRows,
      [newRowKey]: {
        isNewRow: true,
        crossChannelKpiId: newRowKey,
        crossChannelKpiLabel: '',
        mappings: {}
      }
    }));
  }, []);

  const deleteNewRow = useCallback((id: string) => {
    setNewRows(prevNewRows => {
      const { [id]: _, ...rest } = prevNewRows;
      return rest;
    });
  }, []);

  const clearEdits = useCallback(() => {
    setKpiEdits({});
    setNewRows({});
    setCrossChannelKpiNameEdits({});
    setLagEdits({});
    setCrossChannelKpisToDelete({});
    setEditMode(false);
  }, [setEditMode]);

  const saveChanges = useCallback(async () => {
    try {
      setSaving(true);
      try {
        await setAreYouSure({
          title: 'Are you sure?',
          message: 'Are you sure you want to save these changes?',
          okayText: 'Save'
        });
      } catch {
        setSaving(false);
        return;
      }

      const flattenedKpiEdits = Object.values(kpiEdits).flat();

      await CrossChannelLambdaFetch('/setKpiMappings', {
        method: 'POST',
        body: {
          kpiEdits: flattenedKpiEdits,
          crossChannelKpiNameEdits,
          newRows: Object.values(newRows),
          lagEdits,
          crossChannelKpisToDelete: Object.values(crossChannelKpisToDelete),
          company
        }
      });

      setSaving(false);
      clearEdits();
      setKpiMappings(undefined);
    } catch (e) {
      setSaving(false);
      setError({
        message: `Failed to save KPI mapping edits. Error: ${e.message}`,
        reportError: e
      });
    }
  }, [
    kpiEdits,
    crossChannelKpiNameEdits,
    newRows,
    lagEdits,
    crossChannelKpisToDelete,
    company,
    clearEdits,
    setKpiMappings,
    setAreYouSure,
    setError
  ]);

  const combinedCrossChannelKpis: CombinedCrossChannelKpis = useMemo(() => {
    const existingCrossChannelKpis = Object.values(data)
      .map(kpiObj => ({
        crossChannelKpiLabel: kpiObj.crossChannelKpiLabel,
        crossChannelKpiId: kpiObj.crossChannelKpiId,
        isNewRow: kpiObj.isNewRow
      }))
      .filter(kpiObj => !Object.hasOwn(crossChannelKpisToDelete, kpiObj.crossChannelKpiId))
      .sort((a, b) => (a.crossChannelKpiId as number) - (b.crossChannelKpiId as number));

    const newCrossChannelKpis = Object.values(newRows)
      .map(kpiObj => ({
        crossChannelKpiLabel: kpiObj.crossChannelKpiLabel,
        crossChannelKpiId: kpiObj.crossChannelKpiId,
        isNewRow: kpiObj.isNewRow
      }))
      .sort((a, b) => String(a.crossChannelKpiId).localeCompare(String(b.crossChannelKpiId)));

    return [...existingCrossChannelKpis, ...newCrossChannelKpis];
  }, [crossChannelKpisToDelete, data, newRows]);

  const combinedKpiMappings = useMemo(() => ({ ...data, ...newRows }), [data, newRows]);

  const refreshCrossChannel = useCallback(() => {
    (async () => {
      try {
        setRefreshing(true);
        Mixpanel.track(MxE.RefreshCcViews);
        const res = await CrossChannelLambdaFetch('/refreshCrossChannelViews', {
          method: 'POST',
          body: { company }
        });
        const resJson = await awaitJSON(res);
        const { message, status } = resJson;
        setRefreshing(false);
        setError({
          intent: status === 'IN_PROGRESS' ? 'warning' : 'success',
          title: status === 'IN_PROGRESS' ? 'Publish already in progress' : 'Publish started',
          message: <div>{message}</div>
        });
      } catch (e) {
        setRefreshing(false);
        setError({
          message: `Failed to publish to dashboards for ${company}. Error: ${e.message}`,
          reportError: e
        });
      }
    })();
  }, [company, setError]);

  const isNOC = useAppSelector(UserRedux.roleSelector) === 3;

  const hasNoEdits =
    R.isEmpty(kpiEdits) &&
    R.isEmpty(crossChannelKpiNameEdits) &&
    R.isEmpty(newRows) &&
    R.isEmpty(lagEdits) &&
    R.isEmpty(crossChannelKpisToDelete);

  return (
    <>
      <div className='edit-kpi-mapping-container' data-testid='edit-kpi-mapping'>
        <div className='kpi-mapping-edit-controls'>
          {editMode ? (
            <Button size={Size.LG} leadingElement={<MdAdd />} onClick={addNewRow} disabled={saving}>
              Add KPI
            </Button>
          ) : (
            <Button
              size={Size.LG}
              className='edit-mode-button'
              leadingElement={<MdEdit />}
              onClick={() => setEditMode(!editMode)}
              disabled={isNOC}
            >
              Edit
            </Button>
          )}
          <OverlayTrigger
            placement={Placement.TOP_END}
            delay={500}
            overlay={
              <Tooltip>
                Publish the latest mapping changes to dashboards now.{'\n'}Changes will auto-update
                by the next day if not manually published
              </Tooltip>
            }
          >
            <Button
              size={Size.LG}
              appearance={Appearance.OUTLINED}
              leadingElement={<MdSend color={Brand60} />}
              onClick={() => refreshCrossChannel()}
              disabled={isNOC}
              loading={refreshing}
            >
              Publish to dashboards
            </Button>
          </OverlayTrigger>
        </div>
        <div className='edit-section'>
          <div className='cross-channel-kpis-column'>
            <CrossChannelKpiColumn
              crossChannelKpis={combinedCrossChannelKpis}
              crossChannelKpiNameEdits={crossChannelKpiNameEdits}
              handleCrossChannelKpiNameEdit={handleCrossChannelKpiNameEdit}
              handleNewRow={handleNewRow}
              deleteNewRow={deleteNewRow}
              setCrossChannelKpisToDelete={setCrossChannelKpisToDelete}
            />
          </div>
          <div className='scrollable-section'>
            {sourcesWithKpis.map(source => (
              <SourceColumn
                key={source}
                combinedKpiMappings={combinedKpiMappings}
                source={source}
                crossChannelKpis={combinedCrossChannelKpis}
                editsMap={kpiEdits}
                newRows={newRows}
                accounts={accountInfoBySource[source] || []}
                lagEdits={lagEdits}
                handleKpiEdit={handleKpiEdit}
                handleNewRow={handleNewRow}
                handleLagEdit={handleLagEdit}
              />
            ))}
          </div>
        </div>
      </div>
      <div className='footer-controls'>
        {editMode && (
          <div className='right-controls'>
            <Button
              size={Size.LG}
              appearance={Appearance.OUTLINED}
              leadingElement={<MdDelete style={{ color: Brand50 }} />}
              onClick={clearEdits}
              disabled={saving}
            >
              Discard Edits
            </Button>
            <Button
              size={Size.LG}
              leadingElement={<MdSave />}
              onClick={saveChanges}
              disabled={hasNoEdits || saving}
              loading={saving}
            >
              Commit
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export { EditKpiMapping };
